import React, { useContext, useEffect, useState } from 'react';
import { toJS } from 'mobx';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import i18nContext from '../i18n-context';
import { FeesTable } from './FeesTable';
import { ReactComponent as Payments } from 'assets/payments.svg';
import { ReactComponent as Services } from 'assets/services.svg';
import Loader from 'components/common/Loader';
import { getAccountsOptionsForSelect, parseDate } from 'components/utils';
import { isTokenValid } from 'services/authUtils';
import { useMediaQuery } from 'services/useMediaQUery';
import { formPaymentMethod, formServiceName, formTransactionType, isCryptoProvider, MEDIA_QUERY } from 'services/utils';
import { NewContainer } from 'uikit/NewContainer/NewContainer';
import { IconButton } from 'uikit/IconButton/IconButton';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import './Fees.scss';

const Fees = ({ userStore }) => {
  const i18n = useContext(i18nContext);
  const isMobile = useMediaQuery(MEDIA_QUERY.MOBILE);
  const [transactional, setTransactional] = useState(true); // is payments tab active
  const [accountNumber, setAccountNumber] = useState(userStore.currentAccount?.wallet_number);

  useEffect(() => {
    if (isTokenValid() && userStore.activeWallets.length > 0) {
      userStore.resetEnabledTariffs();
      userStore.loadEnabledTariffs(accountNumber, transactional);
    }
  }, [accountNumber, transactional]);

  // Input dropdown is same as in PaymentForm, might encapsulate that component with logic separately to not repeat the code
  const handleChange = (name, data) => {
    const selectedAccount = userStore.activeWallets.find(({ wallet_number }) => wallet_number === data);
    userStore.setCurrentAccount(selectedAccount);
    setAccountNumber(selectedAccount.wallet_number);
  };

  const accountsOptions = getAccountsOptionsForSelect(userStore.activeWallets);

  const mobileText = {
    paymentMethod: i18n.getMessage('fees.paymentMethod'),
    type: i18n.getMessage('fees.type'),
    tariffName: i18n.getMessage('fees.tariffName'),
    tariffValue: i18n.getMessage('fees.tariffValue'),
    dateLastUpdates: i18n.getMessage('fees.dateLastUpdates')
  };

  return (
    <section className={'page-container fees'}>
      <NewContainer className={'fees-container content-card-wrapper'} header={i18n.getMessage('container.fees')}>
        <div className='tabs-container'>
          <IconButton
            className={`fees-tab ${transactional ? 'active' : ''}`}
            active={transactional}
            onClick={() => setTransactional(true)}
            Icon={Payments}
            text={i18n.getMessage('fees.tab.payments')}
          />
          <IconButton
            className='fees-tab'
            active={!transactional}
            onClick={() => setTransactional(false)}
            Icon={Services}
            text={i18n.getMessage('fees.tab.services')}
          />
        </div>
        <div className='dropdown-container'>
          {transactional && (
            <InputDropDown
              label={i18n.getMessage('accounts.iban')}
              className='fees-dropdown'
              options={accountsOptions}
              value={userStore.currentAccount?.wallet_number}
              currency={userStore.currentAccount?.currency}
              networkCode={userStore.currentAccount?.network || null}
              onChange={handleChange}
              isMulti={false}
              isCryptoWallet={isCryptoProvider(userStore.currentAccount?.transfer_provider)}
            />
          )}
        </div>
        <div className='fees-list'>
          {!isMobile && (
            <div className='fees-head'>
              {transactional ? (
                <>
                  <p className='head-item'>{i18n.getMessage('fees.paymentMethod')}</p>
                  <p className='head-item'>{i18n.getMessage('fees.type')}</p>
                </>
              ) : (
                <p className='head-item'>{i18n.getMessage('fees.tariffName')}</p>
              )}
              <p className='head-item'>{i18n.getMessage('fees.tariffValue')}</p>
              <p className='head-item'>{i18n.getMessage('fees.dateLastUpdates')}</p>
            </div>
          )}
          {
            !userStore.isLoading ? (
              userStore.enabledTariffs?.map((data) => (
                <FeesTable
                  mobileText={mobileText}
                  type={'list'}
                  key={data.name} // need change it with better identificator
                  transactional={transactional}
                  paymentMethod={formPaymentMethod(data)}
                  transactionType={formTransactionType(data)}
                  tariffName={formServiceName(data)}
                  lastUpdate={parseDate(data.updated_at)}
                  feeType={data.fee_type}
                  tariffData={[toJS(data)]}
                />
              ))
            ) : (
              <Loader />
            ) // need to separate account and tariffs logic from userStore
          }
        </div>
      </NewContainer>
    </section>
  );
};

Fees.propTypes = {
  userStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  userStore: stores.userStore
}))(observer(Fees));
